import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { extractId, getSchoolUrl } from "@/lib/slug";
import SEO from "@/components/site/SEO";
import JsonLd, { schoolSchema } from "@/components/site/JsonLd";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { StarRating, scoreBand } from "@/components/site/Stars";
import OverallScoreCard from "@/components/site/schooldetail/OverallScoreCard";
import SchoolBreadcrumb from "@/components/site/SchoolBreadcrumb";
import JenjangTabs from "@/components/site/JenjangTabs";
import BiayaPendidikanCard from "@/components/site/BiayaPendidikanCard";
import SchoolStatsPanel from "@/components/site/schooldetail/SchoolStatsPanel";
import ReviewCard from "@/components/site/schooldetail/ReviewCard";
import Gallery from "@/components/site/Gallery";
import PhotoUploader from "@/components/site/PhotoUploader";
import TrustedBadge from "@/components/site/TrustedBadge";
import PartnerBadge from "@/components/site/PartnerBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import ReviewWizard from "@/components/site/ReviewWizard";
import { useFlags } from "@/lib/flags";
import ScoreLegend from "@/components/site/ScoreLegend";
import { Sparkles, ExternalLink, MapPin, RefreshCw, Trophy, Music, BadgeCheck, ShieldCheck, MessageCircle, Newspaper, ArrowUp, ArrowUpRight, Plus, Info, School, Users, GraduationCap, Scale, Filter, ChevronDown, X, Heart } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";
import { TYPE_LABELS, GRADE_LABELS, GRADE_LABELS_LONG } from "@/lib/taxonomy";
import { formatIDR } from "@/lib/compareStore";
import { promptLogin, promptRegister } from "@/lib/authRedirect";
import { useWishlistIds } from "@/components/site/WishlistButton";

// Aside overall-score card + review card + composite score are now in
// /components/site/schooldetail/ (extracted from this file to keep it below 500 lines).

export default function SchoolDetail() {
  const { id: idParam } = useParams();
  const id = extractId(idParam, "sch");
  const nav = useNavigate();
  const { user } = useAuth();
  const { school_reviews_enabled: reviewsOn, standalone_forum_enabled: forumOn } = useFlags();
  const [rawSchool, setSchool] = useState(null);
  // Editorial pivot: while reviews are disabled, rating/summary fields are masked
  // from every public surface on this page (incl. School JSON-LD aggregateRating).
  const school = React.useMemo(
    () => (!rawSchool || reviewsOn ? rawSchool : { ...rawSchool, avg_rating: 0, review_count: 0, ai_summary: null }),
    [rawSchool, reviewsOn],
  );
  const [reviews, setReviews] = useState([]);
  // Review scope: "unit" (this jenjang only, default) or "campus" (all jenjang in same campus)
  const [reviewScope, setReviewScope] = useState("unit");
  const [campusReviews, setCampusReviews] = useState(null);
  const [threads, setThreads] = useState([]);
  const [articles, setArticles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const reviewFormRef = React.useRef(null);
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all"); // all | parent | student | alumni
  const [levelFilter, setLevelFilter] = useState("all"); // all | TK | SD | SMP | SMA
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [tab, setTab] = useState("overview");
  // Mobile hero heart — display-only indicator that mirrors the wishlist state
  // toggled by the bottom action bar. The single save action lives in that bar
  // per Feb 2026 spec (no duplicate save button on the photo).
  const wishlistIds = useWishlistIds();
  const wishlisted = wishlistIds.includes(id);
  // When the wizard is opened via any CTA, ensure we jump to the reviews tab AND
  // scroll the form into view once it has actually mounted. Using useEffect avoids
  // the race between Radix Tabs mount and requestAnimationFrame.
  React.useEffect(() => {
    if (!showForm) return;
    if (tab !== "reviews") return;
    // Give Radix one tick to mount TabsContent + wizard, then scroll.
    const t = setTimeout(() => {
      if (reviewFormRef.current) {
        reviewFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 60);
    return () => clearTimeout(t);
  }, [showForm, tab]);
  // Deep-link support: `?tab=diskusi` (from forum feed school badges) opens the
  // "Diskusi Komunitas" tab directly. Also accepts the raw internal id "discussion".
  // `?review=1` deep-links straight to the Review Wizard so the composer state
  // is URL-addressable (survives OAuth redirects). See openWizard/closeWizard.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const raw = (searchParams.get("tab") || "").toLowerCase();
    const map = { diskusi: "discussion", discussion: "discussion", review: "reviews", reviews: "reviews",
                  informasi: "overview", overview: "overview", artikel: "articles", articles: "articles" };
    const allowed = (t) => (t === "reviews" ? reviewsOn : t === "discussion" ? forumOn : true);
    if (map[raw] && allowed(map[raw])) setTab(map[raw]);
    // Auto-open the Review Wizard when the URL carries the review intent
    // (either from a fresh CTA click or from a post-OAuth redirect).
    if (reviewsOn && searchParams.get("review") === "1") {
      setTab("reviews");
      setShowForm(true);
    }
  }, [searchParams, reviewsOn, forumOn]);

  // Open/close helpers keep the `?review=1` URL param in sync with wizard state
  // so refresh reopens the composer and the intent survives external redirects.
  const openWizard = React.useCallback(() => {
    if (!reviewsOn) return;
    setTab("reviews");
    setShowForm(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.get("review") !== "1") next.set("review", "1");
      return next;
    }, { replace: true });
  }, [setSearchParams, reviewsOn]);
  const closeWizard = React.useCallback(() => {
    setShowForm(false);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.has("review")) next.delete("review");
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [highlightedReviewId, setHighlightedReviewId] = useState(null);
  const TAB_ORDER = ["overview", reviewsOn && "reviews", forumOn && "discussion", "articles"].filter(Boolean);
  // Swipe handler for content area on mobile
  const touchRef = React.useRef({ startX: 0, startY: 0, moved: false });
  const onSwipeStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { startX: t.clientX, startY: t.clientY, moved: false };
  };
  const onSwipeMove = (e) => {
    if (!touchRef.current) return;
    const t = e.touches[0];
    const dx = Math.abs(t.clientX - touchRef.current.startX);
    const dy = Math.abs(t.clientY - touchRef.current.startY);
    if (dx > 8 && dx > dy) touchRef.current.moved = true;
  };
  const onSwipeEnd = (e) => {
    if (!touchRef.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.startX;
    const dy = t.clientY - touchRef.current.startY;
    if (Math.abs(dx) < 60 || Math.abs(dy) > Math.abs(dx)) return;
    const idx = TAB_ORDER.indexOf(tab);
    if (dx < 0 && idx < TAB_ORDER.length - 1) setTab(TAB_ORDER[idx + 1]);
    else if (dx > 0 && idx > 0) setTab(TAB_ORDER[idx - 1]);
  };

  // New thread modal (from Diskusi Komunitas tab)
  const [threadModalOpen, setThreadModalOpen] = useState(false);
  const [threadForm, setThreadForm] = useState({ title: "", body: "", category: "umum", tags: "" });

  // Review reporting
  const [reportReview, setReportReview] = useState(null);
  const [reportReason, setReportReason] = useState("offensive");
  const [reportDetail, setReportDetail] = useState("");
  const [reportBusy, setReportBusy] = useState(false);

  const submitReviewReport = async () => {
    if (!reportReview) return;
    try {
      setReportBusy(true);
      const res = await api.post(`/reviews/${reportReview.review_id}/report`, { reason: reportReason, detail: reportDetail });
      if (res.data?.already) toast.info("Anda sudah pernah melaporkan review ini");
      else toast.success("Laporan terkirim ke tim moderator");
      setReportReview(null);
      setReportDetail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengirim laporan");
    } finally { setReportBusy(false); }
  };
  const [categories, setCategories] = useState([]);

  const load = async () => {
    const [s, r, ph, th, ar, cat] = await Promise.all([
      api.get(`/schools/${id}`).then((x) => x.data),
      reviewsOn ? api.get(`/schools/${id}/reviews`).then((x) => x.data) : Promise.resolve([]),
      api.get(`/schools/${id}/photos`).then((x) => x.data).catch(() => []),
      forumOn ? api.get("/threads", { params: { school_id: id, limit: 20 } }).then((x) => x.data).catch(() => []) : Promise.resolve([]),
      api.get("/blog", { params: { school_id: id, limit: 12 } }).then((x) => x.data).catch(() => []),
      forumOn ? api.get("/forum/categories").then((x) => x.data).catch(() => []) : Promise.resolve([]),
    ]);
    setSchool(s);
    // view_school fires ONCE per successful School Detail load. De-dup keyed
    // on school_id inside analytics.js survives React StrictMode double-effect
    // and route remounts.
    if (s && s.school_id) {
      track("view_school", { school_id: s.school_id, school_slug: s.slug });
    }
    setReviews(r);
    setPhotos(ph);
    setThreads(th);
    setArticles(ar);
    setCategories(cat);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id, reviewsOn, forumOn]);

  // Lock the underlying page from scrolling while the review modal is open —
  // spec: "halaman di belakang tidak bisa di-scroll". Restores original overflow
  // on unmount so we never leave <body> stuck in a locked state.
  //
  // DEFENSIVE CLEANUP (bug fix, Feb 2026): the ReviewWizard nests a shadcn/Radix
  // <Dialog open={guidelinesOpen}> for the Pedoman Komunitas modal. If a user
  // closes the outer review modal while that inner Radix Dialog is open (or
  // still animating out), Radix's own cleanup can be skipped — leaving
  // `pointer-events: none` and/or the `data-scroll-locked` attribute on
  // <body>. That manifests as the "freeze": the whole page ignores clicks
  // even though the modal is gone. The cleanup below scrubs every side-effect
  // that could keep the page unresponsive.
  useEffect(() => {
    if (!showForm) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
      document.body.style.pointerEvents = "";
      document.body.removeAttribute("data-scroll-locked");
    };
  }, [showForm]);

  // Track a school-detail view (unique per viewer per day, backend enforced)
  useEffect(() => {
    if (!id) return;
    api.post(`/schools/${id}/view`).catch(() => {});
  }, [id]);

  const [related, setRelated] = useState({ similar_schools: [], regional_hot_threads: [], guide_articles: [] });
  useEffect(() => {
    if (!id) return;
    api.get(`/schools/${id}/related`).then((r) => setRelated(r.data)).catch(() => {});
  }, [id]);

  // Deep-link: if URL hash points to a specific review (e.g. #review-abc123),
  // switch to the Review tab, scroll it into view, and briefly highlight it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash || "";
    const m = hash.match(/^#review-([\w-]+)$/);
    if (!m) return;
    const reviewId = m[1];
    if (!reviews.some((r) => r.review_id === reviewId)) return;
    setTab("reviews");
    setHighlightedReviewId(reviewId);
    // Scroll after tab renders
    const timer = setTimeout(() => {
      const el = document.getElementById(`review-${reviewId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
    // Auto-remove highlight after 3.5s
    const clear = setTimeout(() => setHighlightedReviewId(null), 3500);
    return () => { clearTimeout(timer); clearTimeout(clear); };
  }, [reviews]);

  const [regenBusy, setRegenBusy] = useState(false);
  const regen = async () => {
    setRegenBusy(true);
    try {
      const r = await api.post(`/schools/${id}/summary/regenerate`);
      setSchool((s) => ({ ...s, ai_summary: r.data.ai_summary }));
      toast.success("Ringkasan diperbarui");
    } catch { toast.error("Gagal memperbarui"); }
    finally { setRegenBusy(false); }
  };

  const submitThread = async (e) => {
    e.preventDefault();
    if (!user) { promptLogin(nav); return; }
    if (!threadForm.title || !threadForm.body) { toast.error("Judul dan isi wajib diisi"); return; }
    try {
      // Parse comma-separated user-typed tags. Backend automatically appends the
      // permanent `sekolah:{school_id}` tag when school_id is passed — user tags
      // are additive and never override the school context.
      const userTags = (threadForm.tags || "")
        .split(",").map((t) => t.trim().toLowerCase()).filter(Boolean);
      const r = await api.post("/threads", {
        title: threadForm.title, body: threadForm.body, category: threadForm.category,
        school_id: id, tags: userTags,
      });
      toast.success("Diskusi dibuat");
      setThreadModalOpen(false);
      setThreadForm({ title: "", body: "", category: "umum", tags: "" });
      nav(`/forum/${r.data.thread_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  if (!school) return <div className="max-w-4xl mx-auto p-10 text-[#4B5563]">Memuat…</div>;

  // When "campus" scope is chosen, use the merged campus-wide review list; otherwise
  // just this unit's reviews (default).
  const activeReviews = reviewScope === "campus" && Array.isArray(campusReviews) ? campusReviews : reviews;

  const filteredReviews = activeReviews.filter((r) => {
    if (onlyVerified && !r.verified_parent) return false;
    if (roleFilter !== "all" && (r.reviewer_role || "parent") !== roleFilter) return false;
    if (levelFilter !== "all" && (r.education_level_reviewed || "").toUpperCase() !== levelFilter) return false;
    return true;
  });

  const roleCounts = reviews.reduce((acc, r) => {
    const k = r.reviewer_role || "parent"; acc[k] = (acc[k] || 0) + 1; return acc;
  }, {});
  const levelCounts = reviews.reduce((acc, r) => {
    const k = (r.education_level_reviewed || "").toUpperCase();
    if (k) acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const schoolAvailableLevels = (school?.education_levels || (school?.level ? [school.level] : []))
    .map((l) => (l || "").toUpperCase())
    .filter((l) => ["TK", "SD", "SMP", "SMA"].includes(l));
  const uniqueSchoolLevels = Array.from(new Set(schoolAvailableLevels));

  return (
    <div data-testid="school-detail-page" className="max-w-full sm:max-w-7xl sm:mx-auto px-0 sm:px-6 lg:px-10 py-6 sm:py-10 pb-32">
      {(() => {
        const areaLabel = [school.city, school.state].filter(Boolean).join(", ");
        const rating = Number(school.avg_rating || 0).toFixed(1);
        const accr = school.accreditation_grade ? ` Akreditasi ${school.accreditation_grade}` : "";
        const seoTitle = reviewsOn
          ? `Review ${school.name}${areaLabel ? ` ${areaLabel}` : ""} — Rating ${rating}${accr} | The School Reports`
          : `${school.name}${areaLabel ? ` ${areaLabel}` : ""} —${accr ? `${accr} ·` : ""} Profil, Biaya & Kurikulum | The School Reports`;
        const spp = school.spp_min && school.spp_max
          ? `Rp ${(school.spp_min/1e6).toFixed(1)}–${(school.spp_max/1e6).toFixed(1)} jt/bulan`
          : (school.tuition_monthly ? `Rp ${(school.tuition_monthly/1e6).toFixed(1)} jt/bulan` : "—");
        const seoDesc = reviewsOn
          ? `Baca review jujur orang tua, biaya SPP ${spp}, dan statistik akreditasi ${school.name}${areaLabel ? ` di ${areaLabel}` : ""}. ${school.review_count || 0} review · Rating ${rating}/5 · Temukan sekolah terbaik di Indonesia.`
          : `Profil ${school.name}${areaLabel ? ` di ${areaLabel}` : ""}: biaya SPP ${spp}, kurikulum, akreditasi, dan fasilitas sekolah.`;
        const canonical = typeof window !== "undefined"
          ? `${window.location.origin}${getSchoolUrl(school)}`
          : undefined;
        // Breadcrumb JSON-LD uses the SAME long-form URL as the canonical, so
        // Google sees a single consistent school URL across all schema.
        const breadcrumbLd = canonical ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", position: 1, name: "Direktori Sekolah",
              item: typeof window !== "undefined" ? `${window.location.origin}/sekolah` : undefined },
            { "@type": "ListItem", position: 2, name: school.name, item: canonical },
          ],
        } : null;
        return (
          <>
            <SEO title={seoTitle} description={seoDesc} image={school.image_url} type="profile" canonical={canonical} />
            <JsonLd data={schoolSchema(school, canonical, reviews, 5)} />
            {breadcrumbLd ? <JsonLd data={breadcrumbLd} /> : null}
          </>
        );
      })()}
      {/* ==================== MOBILE HERO (redesigned Feb 2026) ==================== */}
      {/* One accent (coral #E07A5F). Order: back → photo → title → jenjang → chips →
          location → meta → CTA. Renders <sm only; ≥sm keeps the desktop header. */}
      <div className="sm:hidden" data-testid="mobile-hero">
        <div className="px-4 pt-1 pb-3">
          <Link to="/sekolah" className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#E07A5F]" data-testid="mobile-back-to-schools">
            <span aria-hidden="true">←</span> Semua sekolah
          </Link>
        </div>
        {/* Photo — first item from centralized photos collection. Heart overlay is
            a DISPLAY-ONLY indicator (reflects wishlist state). The custom bottom
            action bar was removed Feb 2026; the global BottomTabBar now owns the
            bottom of the mobile viewport. */}
        {(photos && photos.length > 0 && photos[0]?.data_url) ? (
          <div className="relative w-full aspect-[16/10] bg-[#F3F1ED] overflow-hidden" data-testid="mobile-hero-photo">
            <img src={photos[0].data_url} alt={school.name} className="w-full h-full object-cover" loading="eager" />
            <div
              data-testid="mobile-hero-save-indicator"
              aria-hidden="true"
              className={"absolute top-3 right-3 w-10 h-10 rounded-full bg-white/95 backdrop-blur border border-[#EAE7E0] shadow-md flex items-center justify-center pointer-events-none " + (wishlisted ? "text-[#E07A5F]" : "text-[#8A8F99]")}
            >
              <Heart size={20} fill={wishlisted ? "#E07A5F" : "none"} strokeWidth={2} />
            </div>
          </div>
        ) : (
          <div className="relative w-full aspect-[16/10] bg-gradient-to-br from-[#F3F1ED] to-[#EAE7E0] flex items-center justify-center" data-testid="mobile-hero-photo-empty">
            <School size={40} className="text-[#8A8F99]" strokeWidth={1.5} />
            <div
              data-testid="mobile-hero-save-indicator"
              aria-hidden="true"
              className={"absolute top-3 right-3 w-10 h-10 rounded-full bg-white/95 backdrop-blur border border-[#EAE7E0] shadow-md flex items-center justify-center pointer-events-none " + (wishlisted ? "text-[#E07A5F]" : "text-[#8A8F99]")}
            >
              <Heart size={20} fill={wishlisted ? "#E07A5F" : "none"} strokeWidth={2} />
            </div>
          </div>
        )}
        {/* Title */}
        <div className="px-4 mt-4">
          <h1 className="font-serif-display text-3xl text-[#1F2937] leading-tight break-words" data-testid="mobile-hero-title">{school.name}</h1>
        </div>
        {/* Jenjang segmented control (only if the campus has ≥2 jenjang siblings) */}
        <div className="px-4 mt-3">
          <JenjangTabs school={school} />
        </div>
        {/* Info chips — coral outline, ONLY info that adds signal (school_type + curriculum).
            Jenjang chips are intentionally omitted; the segmented control above already
            communicates jenjang. Max 3 shown to prevent line-wrap noise. */}
        {(() => {
          const chips = [];
          const typeLabel = TYPE_LABELS[school.school_type];
          if (typeLabel && school.school_type !== "swasta" && school.school_type !== "negeri") {
            chips.push({ key: `type-${school.school_type}`, label: typeLabel });
          }
          (school.curriculum || []).slice(0, 2).forEach((c) => {
            // Prefer parens abbreviation (e.g. "International Baccalaureate (IB)" → "IB")
            // so chips stay short on 390px screens; fall back to the pruned full name.
            const m = c.match(/\(([A-Z0-9]{2,6})\)/);
            let compact = m ? m[1] : c.replace(/\s*\([^)]*\)\s*/g, "").trim();
            // Curriculum names like "Kurikulum Merdeka" already start with "Kurikulum" —
            // strip to avoid the doubled "Kurikulum Kurikulum Merdeka" chip.
            compact = compact.replace(/^kurikulum\s+/i, "");
            chips.push({ key: `curr-${c}`, label: `Kurikulum ${compact}` });
          });
          if (!chips.length) return null;
          return (
            <div className="px-4 mt-3 flex flex-wrap gap-2" data-testid="mobile-hero-chips">
              {chips.map(({ key, label }) => (
                <span key={key} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#FFF6F1] text-[#C25A3E] border border-[#F3C6B3]">{label}</span>
              ))}
            </div>
          );
        })()}
        {/* Location — concise: pin + Kecamatan, Kota + "Lihat peta" (coral link). */}
        {(school.kecamatan || school.city) && (
          <div className="px-4 mt-3 inline-flex items-center gap-1.5 text-sm text-[#4B5563] flex-wrap" data-testid="mobile-hero-location">
            <MapPin size={14} className="text-[#8A8F99] flex-shrink-0" />
            <span className="text-[#1F2937]">
              {[school.kecamatan, school.city].filter(Boolean).join(", ")}
            </span>
            <span className="text-[#8A8F99]">·</span>
            <a href="#map-section" className="text-[#E07A5F] hover:text-[#C25A3E] font-medium" data-testid="mobile-hero-lihat-peta">Lihat peta</a>
          </div>
        )}
        {/* Meta row — rating · reviews | Rp range (single source: BiayaPendidikanCard compact). */}
        {(() => {
          const juta = (n) => (Number(n) / 1e6).toFixed(1).replace(".", ",");
          const ribu = (n) => Math.round(Number(n) / 1e3).toString();
          const fmtOne = (n) => (Number(n) >= 1e6 ? `Rp ${juta(n)} jt` : `Rp ${ribu(n)} rb`);
          let sppText = null;
          if (school.spp_min && school.spp_max) {
            sppText = Number(school.spp_min) === Number(school.spp_max)
              ? `${fmtOne(school.spp_min)}/bulan`
              : `Rp ${juta(school.spp_min)}–${juta(school.spp_max)} jt/bulan`;
          } else if (school.spp_min) {
            sppText = `Mulai ${fmtOne(school.spp_min)}/bulan`;
          } else if (school.monthly_spp_amount) {
            sppText = `${fmtOne(school.monthly_spp_amount)}/bulan`;
          } else if (school.tuition_monthly) {
            sppText = `${fmtOne(school.tuition_monthly)}/bulan`;
          }
          const hasRating = Number(school.avg_rating) > 0 || Number(school.review_count) > 0;
          if (!hasRating && !sppText) return null;
          return (
            <div className="px-4 mt-4 flex items-center gap-2 text-sm flex-wrap" data-testid="mobile-hero-meta">
              {hasRating && (
                <span className="inline-flex items-center gap-1.5 text-[#1F2937]">
                  <StarRating value={school.avg_rating} size={14} showHalf useBandColor coralColor />
                  <span className="tabular-nums font-medium">{Number(school.avg_rating || 0).toFixed(1)}</span>
                  <span className="text-[#4B5563]">· {school.review_count || 0} review</span>
                </span>
              )}
              {hasRating && sppText && <span className="text-[#EAE7E0]" aria-hidden="true">|</span>}
              {sppText && (
                <span className="font-semibold text-[#1F2937] tabular-nums" data-testid="mobile-hero-spp">{sppText}</span>
              )}
            </div>
          );
        })()}
        {/* CTA — full-width coral, single primary action. */}
        {reviewsOn && <div className="px-4 mt-4 mb-2">
          <button
            type="button"
            onClick={openWizard}
            data-testid="mobile-hero-write-review"
            className="w-full h-12 rounded-full bg-[#E07A5F] hover:bg-[#C96D55] active:bg-[#B15A47] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors shadow-[0_4px_12px_-4px_rgba(224,122,95,0.5)]"
          >
            <Plus size={16} strokeWidth={2.5} /> Tulis Review
          </button>
        </div>}
      </div>

      {/* ==================== DESKTOP HEADER (≥sm) ==================== */}
      {/* Header text block — padded on mobile so text doesn't touch the edge */}
      <div className="hidden sm:block px-4 sm:px-0">
      {/* Header */}
      <SchoolBreadcrumb school={school} />
      <JenjangTabs school={school} />
      <Link to="/sekolah" className="text-sm text-[#4B5563] hover:text-[#E07A5F]" data-testid="back-to-schools">← Semua sekolah</Link>
      <div className="mt-4 flex flex-wrap gap-2">
        <span className="pill">{TYPE_LABELS[school.school_type] || school.school_type}</span>
        {school.grade_levels?.map((g) => {
          // Show long form ("Kelompok Bermain (KB)", "Taman Kanak-Kanak (TK)")
          // on the school profile; SD/SMP/SMA stay short. Fallback to raw.
          const key = String(g).toLowerCase();
          const label = GRADE_LABELS_LONG[key] || GRADE_LABELS[key] || g;
          return <span key={g} className="pill">{label}</span>;
        })}
        {school.curriculum?.map((c) => (<span key={c} className="pill" data-testid={`curriculum-${c}`}>{c}</span>))}
      </div>
      <div className="mt-3">
        <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1F2937] leading-tight break-words">{school.name}</h1>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <PartnerBadge school={school} size="lg" />
        <TrustedBadge school={school} size="lg" />
      </div>
      <div className="mt-2 text-[#4B5563] inline-flex items-center gap-2 text-sm">
        <MapPin size={14} />
        <span>
          {school.address_street && <>{school.address_street}, </>}
          {school.kelurahan && <>Kel. {school.kelurahan}, </>}
          {school.kecamatan && <>Kec. {school.kecamatan}, </>}
          {school.city}{school.state ? `, ${school.state}` : ""}
        </span>
      </div>
      <div className="mt-4 flex items-center gap-4 flex-wrap">
        {reviewsOn && <StarRating value={school.avg_rating} size={18} testid="school-detail-rating" showHalf useBandColor />}
        {reviewsOn && <span className="text-sm text-[#4B5563]">{school.avg_rating} · {school.review_count} review</span>}
        {(school.monthly_spp_amount != null || school.tuition_monthly != null) && (
          <span className="text-sm font-semibold text-[#1F2937]" data-testid="school-tuition">{formatIDR(school.monthly_spp_amount ?? school.tuition_monthly)}</span>
        )}
        {school.accreditation_grade && (
          <span data-testid="school-accreditation-hero" className={
            "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border-2 " +
            (school.accreditation_grade === "A" ? "bg-[#ECFDF5] text-[#065F46] border-[#059669]" :
             school.accreditation_grade === "B" ? "bg-[#EFF6FF] text-[#1E3A8A] border-[#3B82F6]" :
             "bg-[#FEF3C7] text-[#92400E] border-[#D97706]")
          }>
            <BadgeCheck size={14} /> Akreditasi {school.accreditation_grade}
            {school.accreditation_year && <span className="ml-1 text-[10px] font-medium normal-case tracking-normal opacity-80">({school.accreditation_year})</span>}
          </span>
        )}
        {school.government_verified && (
          <span
            data-testid="school-government-verified-badge"
            title="NPSN telah diverifikasi terhadap referensi Kemdikbud"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest bg-[#1E3A8A] text-white border-2 border-[#1E3A8A]"
          >
            <ShieldCheck size={13} strokeWidth={2.5} />
            {school.accreditation_grade ? `Akreditasi ${school.accreditation_grade} · ` : ""}Terverifikasi Data Pemerintah
          </span>
        )}
        {school.website && (
          <a href={school.website} target="_blank" rel="noreferrer" className="text-sm text-[#4B5563] hover:text-[#E07A5F] inline-flex items-center gap-1">
            Kunjungi situs <ExternalLink size={12} />
          </a>
        )}
      </div>
      </div>
      {/* Photos are now the single source of truth (school.image_url / gallery_images kept only
          for list cards and admin thumbnail sync — see migrate_school_images_to_photos.py).
          Hidden on mobile — photos[0] already appears in the mobile-hero at the top. */}
      {photos && photos.length > 0 && (
        <div className="mt-6 hidden sm:block">
          <Gallery photos={photos} alt={school.name} onReload={async () => {
            const ph = await api.get(`/schools/${id}/photos`).then((x) => x.data).catch(() => []);
            setPhotos(ph);
          }} />
        </div>
      )}

      {/* ===== SCORE SUMMARY BAND — Overall Score + Kebahagiaan Siswa (two white cards) ===== */}
      {reviewsOn && <div className="mt-6 px-4 sm:px-0" data-testid="score-summary-band">
        {/* LEFT — Overall Score */}
        {(() => {
          const avg = school.avg_rating || 0;
          const band = scoreBand(avg);
          return (
            <div className="bg-white border border-[#EAE7E0] rounded-2xl px-6 py-5 shadow-[0_4px_18px_-4px_rgba(31,41,55,0.08)]" data-testid="score-summary-overall">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-[#D2673E] font-semibold">Overall Score</div>
                <ScoreLegend triggerTestid="summary-arti-skor" />
              </div>
              <div className="mt-3">
                <StarRating value={avg} size={22} showHalf coralColor testid="summary-overall-stars" />
              </div>
              <div className="mt-2 font-serif-display text-4xl leading-none tabular-nums text-[#1F2937]" data-testid="summary-overall-value">
                {avg.toFixed(1)}
                <span className="text-lg text-[#4B5563] font-normal"> / 5.0</span>
              </div>
              <div className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border font-medium"
                   data-testid="summary-overall-band"
                   style={{ backgroundColor: band.bg, borderColor: band.border, color: band.text }}>
                {band.label} · {school.review_count || 0} review
              </div>
            </div>
          );
        })()}

        {/* Kebahagiaan Siswa hero-card removed (Iter 84) — this metric is
            now surfaced only within the "Detail penilaian" rating breakdown
            table inside the Reviews tab, per editorial simplification. */}
      </div>}

      {/* ===== BIAYA & PERFORMA panel removed — data biaya sekarang hanya di sidebar (Tab Informasi Umum > kolom kanan) ===== */}

      {/* ===== TABS ===== */}
      <div className="mt-10 grid lg:grid-cols-[minmax(0,1fr)_320px] gap-8 items-start px-4 sm:px-0 min-w-0" data-testid="school-detail-2col">
        <Tabs value={tab} onValueChange={setTab} className="min-w-0">
        <div className="sticky top-16 z-30 -mx-4 sm:-mx-6 lg:-mx-10 bg-[#F9F8F6]/95 backdrop-blur-sm border-b border-[#EAE7E0] min-w-0 max-w-full overflow-hidden" data-testid="tabs-sticky-wrap">
          <div className="flex items-center gap-2 min-w-0 max-w-full pl-4 sm:pl-6 lg:pl-10 pr-3 sm:pr-4 lg:pr-10">
            {/* Scrollable tabs — flex-1 so the CTA button stays anchored right at every breakpoint */}
            <div className="flex-1 min-w-0 overflow-x-auto no-scrollbar" style={{ WebkitOverflowScrolling: "touch" }}>
              <TabsList className="bg-transparent p-0 flex gap-1 h-auto justify-start rounded-none w-max">
                <TabsTrigger value="overview" data-testid="tab-overview" className="flex-shrink-0 rounded-none border-b-2 border-transparent px-3 py-2.5 data-[state=active]:bg-transparent data-[state=active]:text-[#D2673E] data-[state=active]:border-[#D2673E] data-[state=active]:shadow-none transition-colors"><Info size={12} className="mr-1.5" /> Informasi Umum</TabsTrigger>
                {reviewsOn && <TabsTrigger value="reviews" data-testid="tab-reviews" className="flex-shrink-0 rounded-none border-b-2 border-transparent px-3 py-2.5 data-[state=active]:bg-transparent data-[state=active]:text-[#D2673E] data-[state=active]:border-[#D2673E] data-[state=active]:shadow-none transition-colors"><BadgeCheck size={12} className="mr-1.5" /> Review <span className="ml-1 text-[10px] opacity-70">({reviews.length})</span></TabsTrigger>}
                {forumOn && <TabsTrigger value="discussion" data-testid="tab-discussion" className="flex-shrink-0 rounded-none border-b-2 border-transparent px-3 py-2.5 data-[state=active]:bg-transparent data-[state=active]:text-[#D2673E] data-[state=active]:border-[#D2673E] data-[state=active]:shadow-none transition-colors"><MessageCircle size={12} className="mr-1.5" /> Diskusi Komunitas <span className="ml-1 text-[10px] opacity-70">({threads.length})</span></TabsTrigger>}
                <TabsTrigger value="articles" data-testid="tab-articles" className="flex-shrink-0 rounded-none border-b-2 border-transparent px-3 py-2.5 data-[state=active]:bg-transparent data-[state=active]:text-[#D2673E] data-[state=active]:border-[#D2673E] data-[state=active]:shadow-none transition-colors"><Newspaper size={12} className="mr-1.5" /> Artikel & Berita <span className="ml-1 text-[10px] opacity-70">({articles.length})</span></TabsTrigger>
              </TabsList>
            </div>
            {/* Spec §Ringkasan-perubahan — CTA "Tulis Review" REMOVED from tab strip.
                It now lives inside the Reviews section header so mobile users don't
                see it truncated between tab labels. */}
          </div>
        </div>
        {/* Swipeable content wrapper — mobile users can swipe left/right to change tabs */}
        <div
          onTouchStart={onSwipeStart}
          onTouchMove={onSwipeMove}
          onTouchEnd={onSwipeEnd}
          data-testid="tabs-swipe-wrapper"
        >

        {/* === Overview === */}
        <TabsContent value="overview" className="mt-6" data-testid="overview-panel">
          <div className="space-y-6">
            {/* On mobile, show OverallScoreCard + BiayaPendidikanCard (sidebar hidden below lg) */}
            <div className="lg:hidden space-y-6">
              <BiayaPendidikanCard school={school} />
              {reviewsOn && <OverallScoreCard school={school} hideOverall />}
            </div>
            <p className="text-[#4B5563] leading-relaxed">{school.description}</p>

            {/* === Kepadatan Kelas: Jumlah Siswa, Jumlah Guru, Rasio === */}
            {(school.students_count || school.teachers_count) && (
              <SchoolStatsPanel school={school} />
            )}

              {(school.extracurriculars?.length > 0 || school.achievements?.length > 0) && (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="school-ecs-achievements">
                  {school.extracurriculars?.length > 0 && (
                    <div className="bg-white border border-[#EAE7E0] rounded-2xl p-5">
                      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#1E3A8A]">
                        <Music size={12} /> Ekstrakurikuler
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {school.extracurriculars.map((e) => (
                          <span key={e} data-testid={`school-ekskul-${e}`} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-[#F3F1ED] text-[#1F2937] border border-[#EAE7E0]">{e}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {school.achievements?.length > 0 && (
                    <div className="bg-white border border-[#EAE7E0] rounded-2xl p-5">
                      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#E07A5F]">
                        <Trophy size={12} /> Prestasi
                      </div>
                      <ul className="mt-3 space-y-1.5 text-sm text-[#1F2937]">
                        {school.achievements.map((a) => (
                          <li key={a} data-testid={`school-prestasi-${a}`} className="flex items-start gap-2">
                            <span className="text-[#E07A5F] mt-1">•</span> {a}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {reviewsOn && <div className="bg-[#F3F1ED] border border-[#EAE7E0] rounded-2xl p-6" data-testid="ai-summary-block">
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#1E3A8A]">
                    <Sparkles size={14} className="text-[#E07A5F]" /> Ringkasan dari review
                  </div>
                  <button onClick={regen} disabled={regenBusy} data-testid="regen-summary-btn" className="text-xs text-[#4B5563] hover:text-[#1F2937] inline-flex items-center gap-1 disabled:opacity-50">
                    <RefreshCw size={12} /> Perbarui
                  </button>
                </div>
                <p className="mt-3 text-[#1F2937] leading-relaxed">
                  {school.ai_summary || (reviews.length < 2 ? "Butuh minimal 2 review agar bisa dirangkum. Jadilah suara kedua untuk sekolah ini." : "Sedang menyusun ringkasan… tekan Perbarui sebentar lagi.")}
                </p>
              </div>}
          </div>
        </TabsContent>

        {/* === Reviews === */}
        <TabsContent value="reviews" className="mt-6" data-testid="reviews-panel">
          {/* Spec §Rincian-penilaian-hilang-di-mobile — aggregate score panel now
              renders as a COLLAPSIBLE card at the top of the mobile Reviews tab so
              users don't lose the 6-dimension breakdown when jumping straight to reviews. */}
          <details className="lg:hidden mb-5 bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden group" data-testid="mobile-stats-collapsible">
            <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-2 hover:bg-[#F9F8F6]">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-serif-display text-2xl text-[#1F2937]">{(school.avg_rating || 0).toFixed(1)}</span>
                <span className="text-xs text-[#4B5563]">Rincian penilaian per aspek · {reviews.length} review</span>
              </div>
              <ChevronDown size={16} className="text-[#4B5563] transition-transform group-open:rotate-180 flex-shrink-0" />
            </summary>
            <div className="px-2 pb-2">
              <OverallScoreCard school={school} hideOverall />
            </div>
          </details>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-serif-display text-2xl lg:text-3xl text-[#1F2937]">Review</h2>
            <div className="flex items-center gap-2">
              {/* Spec — the primary "Tulis Review" CTA now lives here in the section header, coral pill. */}
              {!showForm && (
                <button
                  type="button"
                  onClick={() => {
                    // Guest-friendly (Feb 2026): let anyone open the wizard.
                    // Auth is enforced inside `ReviewWizard.submit()` — the
                    // reviewer's draft + intent are persisted before redirect.
                    openWizard();
                    setTimeout(() => { reviewFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 100);
                  }}
                  data-testid="section-write-review-btn"
                  aria-label="Tulis Review"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#D2673E] hover:bg-[#B85838] text-white text-sm font-semibold shadow-[0_2px_10px_-2px_rgba(210,103,62,0.5)] transition-colors"
                >
                  <Plus size={14} strokeWidth={2.5} /> Tulis Review
                </button>
              )}
              {showForm && (
                <button type="button" onClick={closeWizard} data-testid="cancel-review-btn"
                  className="text-sm text-[#4B5563] hover:text-[#D2673E] underline-offset-4 hover:underline transition-colors">
                  Batal
                </button>
              )}
            </div>
          </div>
          {/* ReviewWizard renders as a modal overlay per spec — dark backdrop + centered
              scrollable panel + body scroll lock. See <ReviewWizardModal> below the return. */}

          {/* Spec §Filter-menumpuk — MOBILE: chip audiens 1-line horizontal scroll + Filter sheet
              button. The Sheet houses jenjang filter + "hanya wali terverifikasi" toggle. */}
          {reviews.length > 0 && (
            <div className="mt-5 flex items-center gap-2 lg:hidden" data-testid="mobile-filter-bar">
              <div
                className="flex-1 min-w-0 overflow-x-auto no-scrollbar flex items-center gap-2"
                style={{ WebkitOverflowScrolling: "touch" }}
                data-testid="mobile-role-chip-scroller"
              >
                {[
                  { v: "all", label: "Semua", n: reviews.length },
                  { v: "parent", label: "Orang Tua", n: roleCounts.parent || 0 },
                  { v: "student", label: "Siswa", n: roleCounts.student || 0 },
                  { v: "alumni", label: "Alumni", n: roleCounts.alumni || 0 },
                ].map(({ v, label, n }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setRoleFilter(v)}
                    data-testid={`mobile-role-filter-${v}`}
                    className={"flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap " + (roleFilter === v
                      ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                      : "bg-white text-[#4B5563] border-[#EAE7E0]")}
                  >
                    {label} <span className="opacity-70 ml-0.5">({n})</span>
                  </button>
                ))}
              </div>
              <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
                <SheetTrigger asChild>
                  <button
                    type="button"
                    data-testid="mobile-filter-sheet-btn"
                    className="relative flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border bg-white text-[#4B5563] border-[#EAE7E0] hover:border-[#1E3A8A]/40"
                  >
                    <Filter size={12} /> Filter
                    {(onlyVerified || levelFilter !== "all") && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#D2673E]" aria-label="Filter aktif" data-testid="mobile-filter-active-dot" />
                    )}
                  </button>
                </SheetTrigger>
                <SheetContent side="bottom" className="rounded-t-2xl max-h-[70vh] overflow-y-auto" data-testid="mobile-filter-sheet">
                  <SheetHeader>
                    <SheetTitle className="font-serif-display text-xl text-[#1F2937]">Filter review</SheetTitle>
                  </SheetHeader>
                  <div className="mt-5 space-y-5">
                    <label className="flex items-center justify-between gap-3 p-3 rounded-xl border border-[#EAE7E0] cursor-pointer">
                      <span className="inline-flex items-center gap-2 text-sm text-[#1F2937]">
                        <BadgeCheck size={16} className="text-[#166534]" /> Hanya wali terverifikasi
                      </span>
                      <input
                        type="checkbox"
                        checked={onlyVerified}
                        onChange={(e) => setOnlyVerified(e.target.checked)}
                        data-testid="mobile-only-verified-toggle"
                        className="w-4 h-4 accent-[#166534]"
                      />
                    </label>
                    {uniqueSchoolLevels.length > 1 && (
                      <div>
                        <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-2">Sumber Jenjang</div>
                        <div className="flex flex-wrap gap-2">
                          <button type="button" onClick={() => setLevelFilter("all")} data-testid="mobile-level-filter-all"
                            className={"px-3 py-1.5 rounded-full text-xs font-medium border " + (levelFilter === "all"
                              ? "bg-[#7C2D12] text-white border-[#7C2D12]"
                              : "bg-white text-[#4B5563] border-[#EAE7E0]")}>
                            Semua
                          </button>
                          {uniqueSchoolLevels.map((lv) => (
                            <button key={lv} type="button" onClick={() => setLevelFilter(lv)} data-testid={`mobile-level-filter-${lv}`}
                              className={"px-3 py-1.5 rounded-full text-xs font-medium border " + (levelFilter === lv
                                ? "bg-[#7C2D12] text-white border-[#7C2D12]"
                                : "bg-white text-[#4B5563] border-[#EAE7E0]")}>
                              {lv} <span className="opacity-70 ml-0.5">({levelCounts[lv] || 0})</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-2">
                      <button
                        type="button"
                        onClick={() => { setOnlyVerified(false); setLevelFilter("all"); }}
                        data-testid="mobile-filter-reset"
                        className="text-xs text-[#B91C1C] hover:underline"
                      >
                        Reset semua filter
                      </button>
                      <button
                        type="button"
                        onClick={() => setFilterSheetOpen(false)}
                        data-testid="mobile-filter-apply"
                        className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-semibold"
                      >
                        Tampilkan hasil
                      </button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          )}

          {/* Desktop-only "hanya wali terverifikasi" toggle (mobile lives inside sheet) */}
          {reviews.length > 0 && (
            <div className="mt-4 hidden lg:flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer" data-testid="only-verified-toggle-label">
                <input type="checkbox" checked={onlyVerified} onChange={(e) => setOnlyVerified(e.target.checked)} data-testid="only-verified-toggle" className="accent-[#166534]" />
                <span className="inline-flex items-center gap-1"><BadgeCheck size={14} className="text-[#166534]" /> Hanya wali terverifikasi</span>
              </label>
            </div>
          )}

          {/* Role filter chips */}
          {reviews.length > 0 && (
            <div className="mt-5 hidden lg:flex flex-wrap items-center gap-2" data-testid="role-filter-chips">
              {[
                { v: "all", label: `Semua Review (${reviews.length})` },
                { v: "parent", label: `Dari Orang Tua (${roleCounts.parent || 0})` },
                { v: "student", label: `Dari Siswa (${roleCounts.student || 0})` },
                { v: "alumni", label: `Dari Alumni (${roleCounts.alumni || 0})` },
              ].map(({ v, label }) => (
                <button key={v} type="button" onClick={() => setRoleFilter(v)} data-testid={`role-filter-${v}`}
                  className={"px-3 py-1.5 rounded-full text-xs font-medium border transition-all " + (roleFilter === v
                    ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                    : "bg-white text-[#4B5563] border-[#EAE7E0] hover:border-[#1E3A8A]/40 hover:text-[#1E3A8A]")}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Scope toggle: this jenjang only vs all jenjang di kampus yang sama */}
          {school.campus_id && String(school.campus_id).startsWith("cmp_") && (
            <div className="mt-3 flex items-center gap-2" data-testid="review-scope-toggle">
              <span className="text-[10px] uppercase tracking-widest text-[#4B5563] mr-1">Sumber Review:</span>
              <button type="button" data-testid="scope-unit" onClick={() => setReviewScope("unit")}
                className={"px-3 py-1 rounded-full text-xs font-medium border transition-all " + (reviewScope === "unit"
                  ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                  : "bg-white text-[#4B5563] border-[#EAE7E0] hover:border-[#1E3A8A]/40")}>
                Jenjang ini saja
              </button>
              <button type="button" data-testid="scope-campus"
                onClick={async () => {
                  setReviewScope("campus");
                  if (campusReviews === null) {
                    try {
                      const sib = await api.get(`/schools/${id}/siblings`).then((x) => x.data);
                      const ids = (sib.siblings || []).map((s) => s.school_id);
                      const bundles = await Promise.all(
                        ids.map((sid) => api.get(`/schools/${sid}/reviews`).then((x) => x.data).catch(() => []))
                      );
                      const merged = [].concat(...bundles).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
                      setCampusReviews(merged);
                    } catch { setCampusReviews([]); }
                  }
                }}
                className={"px-3 py-1 rounded-full text-xs font-medium border transition-all " + (reviewScope === "campus"
                  ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                  : "bg-white text-[#4B5563] border-[#EAE7E0] hover:border-[#1E3A8A]/40")}>
                Semua jenjang di kampus ini
              </button>
            </div>
          )}

          {/* Jenjang (education-level) filter — DESKTOP ONLY (mobile has it in the Sheet) */}
          {reviews.length > 0 && uniqueSchoolLevels.length > 1 && (
            <div className="mt-3 hidden lg:flex flex-wrap items-center gap-2" data-testid="level-filter-chips">
              <span className="text-[10px] uppercase tracking-widest text-[#4B5563] mr-1">Jenjang:</span>
              <button type="button" onClick={() => setLevelFilter("all")} data-testid="level-filter-all"
                className={"px-3 py-1 rounded-full text-xs font-medium border transition-all " + (levelFilter === "all"
                  ? "bg-[#7C2D12] text-white border-[#7C2D12]"
                  : "bg-white text-[#4B5563] border-[#EAE7E0] hover:border-[#7C2D12]/40")}>
                Semua
              </button>
              {uniqueSchoolLevels.map((lv) => (
                <button key={lv} type="button" onClick={() => setLevelFilter(lv)} data-testid={`level-filter-${lv}`}
                  className={"px-3 py-1 rounded-full text-xs font-medium border transition-all " + (levelFilter === lv
                    ? "bg-[#7C2D12] text-white border-[#7C2D12]"
                    : "bg-white text-[#4B5563] border-[#EAE7E0] hover:border-[#7C2D12]/40")}>
                  {lv} ({levelCounts[lv] || 0})
                </button>
              ))}
            </div>
          )}
          <div className="mt-6 space-y-4">
            {reviews.length === 0 ? (
              <div className="text-[#4B5563] text-sm" data-testid="reviews-empty">Jadilah yang pertama memberi review.</div>
            ) : filteredReviews.length === 0 ? (
              <div className="text-[#4B5563] text-sm" data-testid="reviews-empty-verified">Belum ada review dari wali murid terverifikasi. Coba nonaktifkan filter untuk melihat semua review.</div>
            ) : filteredReviews.map((r, i) => (
              <ReviewCard key={r.review_id} r={r} i={i} photos={photos} user={user}
                          schoolName={school.name}
                          highlighted={highlightedReviewId === r.review_id}
                          onReport={(rev) => setReportReview(rev)}
                          onLoginRequired={() => promptLogin(nav)} />
            ))}
          </div>
        </TabsContent>

        {/* === Discussion === */}
        <TabsContent value="discussion" className="mt-6" data-testid="discussion-panel">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-serif-display text-2xl lg:text-3xl text-[#1F2937]">Diskusi tentang {school.name}</h2>
              <p className="text-sm text-[#4B5563] mt-1">Percakapan orang tua khusus sekolah ini.</p>
            </div>
            <Button onClick={() => (user ? setThreadModalOpen(true) : promptLogin(nav))} data-testid="new-school-thread-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white">
              <Plus size={14} className="mr-1" /> Mulai diskusi
            </Button>
          </div>
          <ul className="mt-6 divide-y divide-[#EAE7E0] bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden">
            {threads.slice(0, 5).map((t, i) => (
              <li key={t.thread_id} data-testid={`school-thread-${i}`} className="p-5 hover:bg-[#FAFAF8]">
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center min-w-[52px] text-[#1E3A8A]">
                    <ArrowUp size={16} />
                    <span className="text-sm font-semibold">{t.upvotes}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/forum/${t.thread_id}`} className="text-[#1F2937] hover:text-[#E07A5F] leading-snug font-medium">{t.title}</Link>
                    <div className="text-xs text-[#4B5563] mt-1 line-clamp-2">{t.body}</div>
                    <div className="text-xs text-[#4B5563] mt-2 inline-flex items-center gap-3">
                      <span>oleh {t.user_name}</span>
                      <span className="inline-flex items-center gap-1"><MessageCircle size={11} /> {t.reply_count}</span>
                    </div>
                  </div>
                </div>
              </li>
            ))}
            {threads.length === 0 && <li className="p-8 text-center text-[#4B5563]" data-testid="school-threads-empty">Belum ada diskusi. Jadilah yang pertama memulai percakapan.</li>}
          </ul>
          {threads.length > 5 && (
            <div className="mt-4 text-center">
              <Link to={`/forum?school_id=${id}`} className="text-sm text-[#1E3A8A] hover:text-[#E07A5F]">Lihat semua {threads.length} diskusi →</Link>
            </div>
          )}
        </TabsContent>

        {/* === Articles === */}
        <TabsContent value="articles" className="mt-6" data-testid="articles-panel">
          <h2 className="font-serif-display text-2xl lg:text-3xl text-[#1F2937]">Artikel & berita terkait</h2>
          <p className="text-sm text-[#4B5563] mt-1">Artikel editorial dan advertorial dari The Reports yang membahas sekolah ini.</p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.map((a, i) => (
              <Link to={`/jurnal/${a.slug}`} key={a.post_id} data-testid={`school-article-${i}`}
                    className="group bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden hover:border-[#1E3A8A] transition-colors">
                <div className="aspect-[16/9] bg-[#F3F1ED] overflow-hidden">
                  {a.cover_url ? <img src={a.cover_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <div className="w-full h-full flex items-center justify-center"><Newspaper size={24} className="text-[#4B5563]" /></div>}
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">{new Date(a.created_at).toLocaleDateString("id-ID")}</div>
                  <div className="mt-1 text-[#1F2937] font-medium group-hover:text-[#1E3A8A] line-clamp-2">{a.title}</div>
                  <div className="text-xs text-[#4B5563] mt-1 line-clamp-2">{a.excerpt}</div>
                </div>
              </Link>
            ))}
            {articles.length === 0 && <div className="col-span-full text-center p-8 text-[#4B5563] bg-white border border-[#EAE7E0] rounded-2xl" data-testid="school-articles-empty">Belum ada artikel yang di-tag ke sekolah ini.</div>}
          </div>
        </TabsContent>
        </div>
      </Tabs>

      {/* ===== RIGHT SIDEBAR (desktop only) — Biaya + Rincian Penilaian + NPS ===== */}
      <aside
        className="hidden lg:block sticky top-16 space-y-4"
        data-testid="school-detail-sidebar"
      >
        {/* Biaya Pendidikan card (new — uses range + first-year estimate + academic year metadata) */}
        <BiayaPendidikanCard school={school} />

        {reviewsOn && <OverallScoreCard school={school} hideOverall />}
      </aside>
      </div>

      {/* Cross-Discovery Phase 1 — School → Articles.
          Highlight top 3 published Journal articles that explicitly reference
          this school via `related_school_ids`. Fed from the same `articles`
          state that powers the Articles tab (canonical `/api/blog?school_id=X`
          which already applies `public_blog_filter`). Section hides entirely
          when there are no related articles, so schools with no editorial
          coverage look identical to before. */}
      {articles && articles.length > 0 && (
        <section className="mt-16 pt-10 border-t border-[#EAE7E0] px-4 sm:px-0" data-testid="school-related-articles">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#E07A5F] font-bold">The School Reports</div>
              <h2 className="mt-1 font-serif-display text-2xl lg:text-3xl text-[#1F2937]">Dari The School Reports</h2>
              <p className="mt-1 text-sm text-[#4B5563]">Artikel editorial The Reports yang menyebut sekolah ini.</p>
            </div>
            {articles.length > 3 && (
              <button
                type="button"
                onClick={() => setTab("articles")}
                data-testid="school-related-articles-see-all"
                className="text-sm font-medium text-[#1E3A8A] hover:text-[#E07A5F] inline-flex items-center gap-1"
              >
                Lihat semua {articles.length} artikel <ArrowUpRight size={14} strokeWidth={2.25} />
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.slice(0, 3).map((a, i) => (
              <Link
                key={a.post_id}
                to={`/jurnal/${a.slug}`}
                data-testid={`school-related-article-${i}`}
                className="group bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden hover:border-[#1E3A8A] transition-colors"
              >
                <div className="aspect-[16/9] bg-[#F3F1ED] overflow-hidden">
                  {a.cover_url
                    ? <img src={a.cover_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    : <div className="w-full h-full flex items-center justify-center"><Newspaper size={24} className="text-[#4B5563]" /></div>}
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">{new Date(a.created_at).toLocaleDateString("id-ID")}</div>
                  <div className="mt-1 text-[#1F2937] font-medium group-hover:text-[#1E3A8A] line-clamp-2">{a.title}</div>
                  <div className="text-xs text-[#4B5563] mt-1 line-clamp-2">{a.excerpt}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CROSS-DISCOVERY: "Jelajahi lebih jauh" — Similar Schools + Regional Hot Threads + Guide Articles */}
      {(related.similar_schools.length + related.regional_hot_threads.length + related.guide_articles.length) > 0 && (
        <section className="mt-16 pt-10 border-t border-[#EAE7E0] px-4 sm:px-0" data-testid="cross-discovery-section">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <h2 className="font-serif-display text-3xl lg:text-4xl text-[#1F2937] mt-1 leading-tight">Temukan Sekolah Lainnya</h2>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            {/* Similar schools — full-width horizontal card grid (matches design) */}
            {related.similar_schools.length > 0 && (
              <div className="lg:col-span-12" data-testid="similar-schools-widget">
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-3 flex items-center gap-1.5"><Sparkles size={11} className="text-[#E07A5F]" /> Sekolah serupa</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {related.similar_schools.slice(0, 4).map((s, i) => (
                    <Link key={s.school_id} to={getSchoolUrl(s)} data-testid={`similar-school-${i}`}
                      className="group bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden hover:border-[#1E3A8A] hover:-translate-y-0.5 transition-all">
                      <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#DBE1EE] to-[#B8C5DB]">
                        {s.image_url && <img src={s.image_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                      </div>
                      <div className="p-4">
                        <div className="text-[10px] uppercase tracking-widest text-[#D2673E] font-semibold">{s.city}</div>
                        <div className="font-serif-display text-lg text-[#1F2937] mt-1 leading-tight group-hover:text-[#1E3A8A] transition-colors line-clamp-2">{s.name}</div>
                        <div className="flex items-center gap-2 mt-3">
                          {reviewsOn && <StarRating value={s.avg_rating} size={13} testid={`similar-school-${i}-rating`} />}
                          {reviewsOn && <span className="text-xs text-[#4B5563]">{s.review_count || 0} review</span>}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Regional hot threads (col 7) */}
            {forumOn && related.regional_hot_threads.length > 0 && (
              <div className="lg:col-span-7" data-testid="regional-threads-widget">
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-3 flex items-center gap-1.5"><MessageCircle size={11} className="text-[#E07A5F]" /> Diskusi hangat sekitar sini</div>
                <div className="space-y-2.5">
                  {related.regional_hot_threads.map((t, i) => (
                    <Link key={t.thread_id} to={`/forum/${t.thread_id}`} data-testid={`regional-thread-${i}`}
                      className="block p-3 bg-white border border-[#EAE7E0] rounded-2xl hover:border-[#1E3A8A] hover:-translate-y-0.5 transition-all group">
                      <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">{t.school_name || t.category}</div>
                      <div className="font-serif-display text-sm text-[#1F2937] leading-snug group-hover:text-[#1E3A8A] transition-colors line-clamp-2 mt-0.5">{t.title}</div>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#4B5563]">
                        <span className="inline-flex items-center gap-1"><ArrowUp size={10} /> {t.upvotes || 0}</span>
                        <span className="inline-flex items-center gap-1"><MessageCircle size={10} /> {t.reply_count || 0} balasan</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Guide articles (col 5) */}
            {related.guide_articles.length > 0 && (
              <div className="lg:col-span-5" data-testid="guide-articles-widget">
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-3 flex items-center gap-1.5"><Newspaper size={11} className="text-[#E07A5F]" /> Panduan editorial</div>
                <div className="space-y-2.5">
                  {related.guide_articles.map((a, i) => (
                    <Link key={a.post_id} to={`/jurnal/${a.slug}`} data-testid={`guide-article-${i}`}
                      className="block bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden hover:border-[#1E3A8A] hover:-translate-y-0.5 transition-all group">
                      {a.cover_url && <div className="aspect-[16/9] overflow-hidden bg-[#F3F1ED]"><img src={a.cover_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
                      <div className="p-3">
                        <div className="font-serif-display text-sm text-[#1F2937] leading-snug group-hover:text-[#1E3A8A] transition-colors line-clamp-3">{a.title}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Thread creation dialog (from Diskusi Komunitas tab) */}
      <Dialog open={threadModalOpen} onOpenChange={setThreadModalOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mulai diskusi tentang {school.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitThread} className="space-y-3 mt-2">
            {/* Locked school context — auto-attached tag */}
            <div
              data-testid="school-thread-context-pill"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F6E7DF] border border-[#F3D9C7] text-[#BE5730] text-[11px] font-medium"
              title="Diskusi ini otomatis ditandai dengan sekolah ini."
            >
              <School size={13} strokeWidth={2.25} />
              <span>Konteks: <span className="font-semibold">{school.name}</span></span>
              <span className="text-[10px] uppercase tracking-widest text-[#8A5B3F]/80 ml-1">Tag otomatis</span>
            </div>
            <Input placeholder="Judul diskusi" value={threadForm.title} onChange={(e) => setThreadForm({ ...threadForm, title: e.target.value })} data-testid="school-thread-title-input" />
            <Textarea rows={5} placeholder="Konteks pertanyaan Anda…" value={threadForm.body} onChange={(e) => setThreadForm({ ...threadForm, body: e.target.value })} data-testid="school-thread-body-input" />
            <Input
              placeholder="Tag tambahan (opsional) — pisahkan dengan koma. mis: kurikulum, jakarta-selatan"
              value={threadForm.tags}
              onChange={(e) => setThreadForm({ ...threadForm, tags: e.target.value })}
              data-testid="school-thread-tags-input"
            />
            <p className="text-[10px] text-[#8A8F99] -mt-1">Tag sekolah asal tidak akan hilang meski Anda menambahkan tag lain.</p>
            <Select value={threadForm.category} onValueChange={(v) => setThreadForm({ ...threadForm, category: v })}>
              <SelectTrigger data-testid="school-thread-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => (<SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button type="submit" data-testid="school-thread-submit-btn" className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">Kirim diskusi</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Review report dialog */}
      <Dialog open={!!reportReview} onOpenChange={(v) => { if (!v) { setReportReview(null); setReportDetail(""); } }}>
        <DialogContent data-testid="review-report-dialog">
          <DialogHeader>
            <DialogTitle>Laporkan review</DialogTitle>
          </DialogHeader>
          {reportReview && (
            <div className="space-y-3">
              <div className="rounded-xl bg-[#F3F1ED] p-3 text-sm">
                <div className="text-[11px] uppercase tracking-widest text-[#4B5563]">Review yang dilaporkan</div>
                <div className="font-serif-display text-base text-[#1F2937] mt-0.5 line-clamp-1">{reportReview.title}</div>
                <div className="text-xs text-[#4B5563] line-clamp-2 mt-0.5">{reportReview.body}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Alasan</div>
                <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} data-testid="review-report-reason"
                  className="w-full h-10 border border-[#EAE7E0] rounded-lg px-3 bg-white text-sm">
                  <option value="offensive">Kasar / menyinggung</option>
                  <option value="spam">Spam atau promosi</option>
                  <option value="fake">Tidak jujur / palsu</option>
                  <option value="privacy">Melanggar privasi anak / keluarga</option>
                  <option value="not_related">Bukan tentang sekolah ini</option>
                  <option value="other">Lainnya</option>
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-1">Detail (opsional)</div>
                <Textarea rows={3} value={reportDetail} onChange={(e) => setReportDetail(e.target.value)} data-testid="review-report-detail" maxLength={400} placeholder="Jelaskan singkat…" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => { setReportReview(null); setReportDetail(""); }} className="rounded-full h-10">Batal</Button>
                <Button disabled={reportBusy} onClick={submitReviewReport} data-testid="review-report-submit"
                  className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-10">
                  {reportBusy ? "Mengirim…" : "Kirim laporan"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bottom action bar removed (Feb 2026) — global BottomTabBar (Beranda | Direktori
          | Diskusi | Jurnal) now owns the bottom of the mobile viewport for every page,
          including school detail. Compare/Save/Share actions were removed with the bar
          per product decision; Tulis Review remains accessible via the mobile hero CTA
          and the section headers. */}

      {/* Review composer MODAL overlay. Renders on top of the page with a dark
          backdrop; the panel inside scrolls independently. Body scroll is locked
          while open via the useEffect above (`document.body.style.overflow`). */}
      {showForm && (
        <div
          className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center sm:p-4"
          data-testid="review-modal-root"
          role="dialog"
          aria-modal="true"
          aria-label="Tulis Review"
        >
          <div
            className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
            onClick={closeWizard}
            data-testid="review-modal-backdrop"
            aria-hidden="true"
          />
          <div
            ref={reviewFormRef}
            className="relative w-full h-full sm:h-auto sm:max-w-3xl sm:max-h-[92vh] overflow-y-auto overscroll-contain bg-white sm:rounded-2xl shadow-2xl"
            data-testid="review-modal-panel"
          >
            {/* Close button lives in its own sticky row so it doesn't need
                `float-right`. The previous float caused ReviewWizard's
                `overflow-hidden` root to become a Block Formatting Context
                and shrink to 346px on 390px viewports — visually reading as
                "modal not full width". Wrapper is `pointer-events-none` so
                the row itself doesn't intercept scrolls/taps. */}
            <div className="sticky top-0 z-10 flex justify-end px-2 pt-2 sm:px-3 sm:pt-3 pointer-events-none">
              <button
                type="button"
                onClick={closeWizard}
                data-testid="review-modal-close"
                aria-label="Tutup form review"
                className="pointer-events-auto w-9 h-9 rounded-full bg-white/95 border border-[#EAE7E0] text-[#4B5563] hover:bg-[#F9F8F6] hover:text-[#1F2937] shadow-md flex items-center justify-center transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <ReviewWizard
              schoolId={id}
              schoolName={school.name}
              schoolLevels={school.education_levels || (school.level ? [school.level] : [])}
              onCancel={closeWizard}
            />
          </div>
        </div>
      )}
    </div>
  );
}
